class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // While closed, the drawer is only visually hidden, so its contents would
    // stay in the tab order. Mark it inert when closed so its links/buttons are
    // skipped by Tab and hidden from assistive tech until the cart is opened.
    this.inert = !this.classList.contains('active');

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('#CartDrawer-Overlay, .drawerCloseButton').forEach((e) => e.addEventListener('click', this.close.bind(this)));
    // The close control is a <div role="button">; activate it via keyboard.
    // Delegated so re-rendered close buttons keep working.
    this.addEventListener('keydown', (evt) => {
      if ((evt.key === 'Enter' || evt.key === ' ' || evt.key === 'Spacebar') &&
          evt.target.closest && evt.target.closest('.drawerCloseButton')) {
        evt.preventDefault();
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelectorAll('.cart-icon-bubble');

    let referThis = this;
    
    cartLink.forEach(function(e) {
      e.setAttribute('role', 'button');
      e.setAttribute('aria-haspopup', 'dialog');
      e.addEventListener('click', (event) => {
        event.preventDefault();
        referThis.open(e)
      });
      e.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          referThis.open(e);
        }
      });
    });
  }

  open(triggeredBy) {
    this.inert = false; // make contents focusable before the focus trap runs
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      this.setupFocusTrap();
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.removeFocusTrap();
    // Return focus to whatever opened the drawer (the cart icon).
    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      this.activeElement.focus();
    }
    document.body.classList.remove('overflow-hidden');
    this.inert = true; // closed again — remove contents from the tab order
  }

  // Visible, operable focusable elements within the drawer, in DOM order.
  getDrawerFocusables() {
    return Array.prototype.filter.call(
      this.querySelectorAll(
        'a[href], button, input:not([type="hidden"]), select, textarea, cart-remove-button, [tabindex]:not([tabindex="-1"]), [role="button"]'
      ),
      (el) => el.offsetParent !== null && !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
    );
  }

  // Keep Tab within the open drawer; wrapping past the last element returns
  // focus to the top of the cart (and vice-versa for Shift+Tab).
  setupFocusTrap() {
    var focusables = this.getDrawerFocusables();
    if (focusables.length) focusables[0].focus();

    this._trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      var f = this.getDrawerFocusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (!this.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this._trapHandler, true);
  }

  removeFocusTrap() {
    if (this._trapHandler) {
      document.removeEventListener('keydown', this._trapHandler, true);
      this._trapHandler = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.contains('is-empty') && this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      if (section.class){
        const sectionElement = document.querySelectorAll(section.class);
        sectionElement.forEach(function(elem) {
          elem.innerHTML =
              this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }.bind(this));
      }else{
        const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);

        sectionElement.innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }

    }));

    this.querySelectorAll('.drawerCloseButton').forEach((e) => e.addEventListener('click', this.close.bind(this)));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble',
        class: '.cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        class: '.cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
